import React, { useRef } from "react";
import useSwr from "swr";
import { LoadingOverlay, Modal, Paper, Tabs, Text } from "@mantine/core";
import { PageLayout } from "../../../components/layout";
import { useNavigate, useParams } from "react-router";
import { ProductsApi } from "../../../apis";
import { BuyerProductMainForm, IBuyerLedFormData } from "../../../components/buyer-product/main-form";
import { Message } from "../../../utils/message";
import HttpHandler from "../../../utils/http-handler";
import { BuyerProductFinanceForm, IBuyerFinanceFormData } from "../../../components/buyer-product/finance-form";
import { BuyerProductFeeForm } from "../../../components/buyer-product/fee-form";
import { RequestPinForm } from "../../../components/buyer-product/request-pin-form";
import { ITableRef } from "../../../components/table";
import { IconCashBanknote, IconFileText, IconMoneybag } from "@tabler/icons";
import { useSelector } from "react-redux";
import { IAuth } from "../../../interfaces/IAuth";

export function BuyerProductEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const { id, index } = params;
  const breadcrumbs = useBreadcrumb();
  const ref = useRef<ITableRef>(null);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const [tabIndex, setTabIndex] = React.useState(index || "1");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [action, setAction] = React.useState<string[]>([]);

  const { data, mutate } = useSwr(`/api/buyer-led/product/${id}`, async () => await ProductsApi.get(`${id}`), {
    fallback: { data: null },
  });

  const onMainSubmit = async (data: IBuyerLedFormData, type: boolean) => {
    setLoading(true);
    try {
      await ProductsApi.update(params.id as string, {
        name: data.name,
        resolutionNo: data.resolutionNo,
        fundType: data.fundType,
        description: data.description,
      });

      await mutate();

      if (type) {
        onTabChange("2");
        navigate(`/scf-dcf-product/dcf-product/edit/${params.id}/2`);
        Message.success("Бүтээгдэхүүнийг амжилттай засварласан");
      } else {
        navigate("/scf-dcf-product/dcf-product");
        Message.success("Бүтээгдэхүүнийг амжилттай засварлан хадгаллаа.");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      Message.error((err as HttpHandler)?.message!);
    }
    setLoading(false);
  };

  const onFinanceSubmit = async (values: IBuyerFinanceFormData, type: boolean) => {
    setLoading(true);
    try {
      await ProductsApi.funding(params.id as string, {
        minInvAmount: values.minInvAmount,
        maxOfInv: values.maxOfInv,
        minOfInv: values.minOfInv,
        minTenor: values.minTenor,
        maxTenor: values.maxTenor,
        minDays: values.minDays,
        maxDays: values.maxDays,
        borrowerType: values.borrowerType,
        recourseTerm: values.recourseTerm,
        suppTerminatePeriod: values.suppTerminatePeriod,
        productOverDueLimit: values.productOverDueLimit,
        overDueLimitType: values.overDueLimitType,
        overDueLimitValue: values.overDueLimitValue,
        buyerTerm: values.buyerTerm,
        buyerTerminatePeriod: values.buyerTerminatePeriod,
        recourseDays: values.recourseDays,
      });

      await mutate();
      if (type) {
        onTabChange("3");
        navigate(`/scf-dcf-product/dcf-product/edit/${params.id}/3`);
        Message.success("Амжилттай санхүүжилт олгох үеийн нөхцөлийг тохируулсан!");
      } else {
        navigate(`/scf-dcf-product/dcf-product`);
        Message.success("Амжилттай санхүүжилт олгох үеийн нөхцөлийг тохируулан хадгалсан!");
      }
    } catch (err) {
      Message.error((err as HttpHandler)?.message!);
    }
    setLoading(false);
  };

  const onFeeSubmit = async (values: any, type: boolean) => {
    setLoading(true);
    try {
      if (type) {
        setAction(["CONTROL", values]);
      } else {
        await ProductsApi.commission(params.id as string, {
          suppFee: values.suppFee,
          suppCalculateDay: values.suppCalculateDay,
          suppAppFee: values.suppAppFee,
          suppDisbursementFee: values.suppDisbursementFee,
          suppFeeRule: values.suppFeeRule,
          suppMinFee: values.suppMinFee,
          suppPenaltyType: values.suppPenaltyType,
          suppPenaltyPercent: values.suppPenaltyPercent,
          buyerFee: values.buyerFee,
          buyerCalculateDay: values.buyerCalculateDay,
          buyerAppFee: values.buyerAppFee,
          buyerDisbursementFee: values.buyerDisbursementFee,
          buyerFeeRule: values.buyerFeeRule,
          buyerPenaltyType: values.buyerPenaltyType,
          buyerPenaltyPercent: values.buyerPenaltyPercent,
          buyerMinFee: values.buyerMinFee,
          toReview: false,
        });

        navigate("/scf-dcf-product/scf/product");
        Message.success("Амжилттай хадгаллаа!");
      }

      setLoading(false);
      await mutate();
    } catch (err) {
      Message.error((err as HttpHandler)?.message!);
    }
    setLoading(false);
  };

  const onTabChange = (_index: string) => {
    navigate(`/scf-dcf-product/dcf-product/edit/${params.id}/${_index}`);
    setTabIndex(_index);
  };

  const reload = () => {
    ref.current?.reload();
  };

  const onCancel = async () => {
    setAction([]);
  };

  return (
    <>
      <PageLayout title="Buyer LED-SCF бүтээгдэхүүн" subTitle="Buyer LED-SCF бүтээгдэхүүнийг засварлах" breadcrumb={breadcrumbs} bgColor="#F8F9FA">
        <Paper radius="sm" p="md" withBorder>
          <Tabs defaultValue="1" value={tabIndex} onTabChange={onTabChange}>
            <Tabs.List>
              <Tabs.Tab value="1" icon={<IconFileText color={tabIndex === "1" ? "#3e166e" : "#44566C"} />}>
                <Text size="sm" weight={500} c="#44566C">
                  Үндсэн мэдээлэл
                </Text>
              </Tabs.Tab>
              {(user?.permissions || []).filter((c) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_BL" && c.isEdit).length > 0 && (
                <Tabs.Tab value="2" icon={<IconMoneybag color={tabIndex === "2" ? "#9F20F7" : "#44566C"} />}>
                  <Text size="sm" weight={500} c="#44566C">
                    Санхүүжилтийн тохиргоо
                  </Text>
                </Tabs.Tab>
              )}

              {(user?.permissions || []).filter((c) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_BL" && c.isEdit).length > 0 && (
                <Tabs.Tab value="3" icon={<IconCashBanknote color={tabIndex === "3" ? "#3e166e" : "#44566C"} />}>
                  <Text size="sm" weight={500} c="#44566C">
                    Шимтгэл тохиргоо
                  </Text>
                </Tabs.Tab>
              )}
            </Tabs.List>
            <Tabs.Panel value="1">
              {tabIndex === "1" && data && (
                <>
                  <LoadingOverlay visible={loading} />
                  <BuyerProductMainForm onSubmit={onMainSubmit} action={["update", data]} />
                </>
              )}
            </Tabs.Panel>
            {(user?.permissions || []).filter((c) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_BL" && c.isEdit).length > 0 && (
              <Tabs.Panel value="2">
                {tabIndex === "2" && data && (
                  <>
                    <LoadingOverlay visible={loading} />
                    <BuyerProductFinanceForm onSubmit={onFinanceSubmit} action={["update", data]} />
                  </>
                )}
              </Tabs.Panel>
            )}
            {(user?.permissions || []).filter((c) => c.module === "PRODUCT" && c.code === "SCF_PRODUCT_MANAGEMENT_BL" && c.isEdit).length > 0 && (
              <Tabs.Panel value="3">
                {tabIndex === "3" && data && (
                  <>
                    <LoadingOverlay visible={loading} />
                    <BuyerProductFeeForm onSubmit={onFeeSubmit} action={["update", data]} />
                  </>
                )}
              </Tabs.Panel>
            )}
          </Tabs>
        </Paper>
      </PageLayout>
      <Modal opened={action[0] === "CONTROL"} onClose={() => setAction && setAction([])} withCloseButton={false}>
        <RequestPinForm
          title="Баталгаажуулах пин код"
          subTitle="Мэдээллээ сайтар нягтлан ПИН код оруулна уу."
          action={action}
          reload={reload}
          onCancel={onCancel}
          type={"BUYER_LED"}
        />
      </Modal>
    </>
  );
}

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Үндсэн",
  },
  {
    label: "Санхүүжилтийн бүтээгдэхүүн",
  },
  {
    to: "/scf-dcf-product/dcf-product",
    label: "Buyer LED бүтээгдэхүүний жагсаалт",
  },
  {
    label: "Засварлах",
  },
];
